import React, { useEffect, useRef, useState } from 'react';
import logo from '../blueclan-logo.png';
import './Hero.css';

const DIVISIONS = ['AI Intelligence', 'Web Fortress', 'Mobile Battalion', 'Cloud Strike Unit'];

function useTypewriter(strings, speed = 75, pause = 2000) {
  const [text, setText] = useState('');
  const [idx, setIdx]   = useState(0);
  const [del, setDel]   = useState(false);

  useEffect(() => {
    const cur = strings[idx % strings.length];
    const t = setTimeout(() => {
      if (!del) {
        setText(cur.slice(0, text.length + 1));
        if (text.length + 1 === cur.length) setTimeout(() => setDel(true), pause);
      } else {
        setText(cur.slice(0, text.length - 1));
        if (text.length === 0) { setDel(false); setIdx(i => i + 1); }
      }
    }, del ? speed / 2 : speed);
    return () => clearTimeout(t);
  }, [text, del, idx, strings, speed, pause]);

  return text;
}

const captains = [
  { division: 'AI Division',  role: 'Captain', color: '#00d4ff', icon: '◈' },
  { division: 'Web Division', role: 'Captain', color: '#7b2fff', icon: '◈' },
  { division: 'App Division', role: 'Captain', color: '#ffd700', icon: '◈' },
];

const heroStats = [
  { val: '3',    lbl: 'Captains' },
  { val: '32+',  lbl: 'Troops' },
  { val: '150+', lbl: 'Missions' },
  { val: '5 Yrs',lbl: 'In Command' },
];

export default function Hero() {
  const canvasRef   = useRef(null);
  const [missions, setMissions] = useState(12);
  const typed = useTypewriter(DIVISIONS);

  /* ── Particle network canvas ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;

    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);

    const pts = Array.from({ length: 70 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      r: Math.random() * 1.4 + 0.3,
      vx: (Math.random() - 0.5) * 0.35,
      vy: (Math.random() - 0.5) * 0.35,
      o: Math.random() * 0.45 + 0.08,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      pts.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0,212,255,${p.o})`;
        ctx.fill();
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width)  p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height)  p.vy *= -1;
      });
      pts.forEach((a, i) => pts.slice(i + 1).forEach(b => {
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        if (d < 100) {
          ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
          ctx.strokeStyle = `rgba(0,212,255,${0.1 * (1 - d / 100)})`;
          ctx.lineWidth = 0.5; ctx.stroke();
        }
      }));
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);

  return (
    <section className="hero" id="home">
      <canvas ref={canvasRef} className="hero-canvas" />
      <div className="grid-bg" />
      <div className="hero-orb hero-orb--cyan" />
      <div className="hero-orb hero-orb--purple" />

      <div className="container hero-grid">

        {/* ── LEFT: Identity ── */}
        <div className="hero-left">
          {/* Status badge */}
          <div className="hero-status-bar animate-in">
            <span className="hsb-dot" />
            <span className="hsb-label">CLAN STATUS:</span>
            <span className="hsb-val">ACTIVE</span>
            <span className="hsb-sep">|</span>
            <span className="hsb-label">TROOPS DEPLOYED:</span>
            <span className="hsb-val">32</span>
          </div>

          {/* Main headline */}
          <h1 className="hero-headline animate-in delay-1">
            <span className="hl-pre">WE ARE</span>
            <span className="hl-clan">BLUE<span className="hl-cyan">CLAN</span></span>
            <span className="hl-typed">
              <span className="hl-division">{typed || ' '}</span>
              <span className="cursor-blink">|</span>
            </span>
          </h1>

          {/* Description */}
          <p className="hero-desc animate-in delay-2">
            An elite technology clan led by battle-hardened{' '}
            <span className="desc-hl">Captains</span> and powered by disciplined{' '}
            <span className="desc-hl">Troops</span>. We engineer AI systems, web
            fortresses, and mobile apps that dominate every battlefield.
          </p>

          {/* CTAs */}
          <div className="hero-ctas animate-in delay-3">
            <a href="#contact" className="btn-primary"><span>Join the Clan</span></a>
            <a href="#team"    className="btn-outline">Meet the Captains</a>
          </div>

          {/* Stat pills */}
          <div className="hero-statpills animate-in delay-4">
            {heroStats.map(s => (
              <div className="stat-pill" key={s.lbl}>
                <span className="sp-val">{s.val}</span>
                <span className="sp-lbl">{s.lbl}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── RIGHT: Command Roster ── */}
        <div className="hero-right animate-in delay-2">
          <div className="roster-card glass-card">

            {/* Card header */}
            <div className="roster-header">
              <div className="roster-title-row">
                <span className="roster-tag">// COMMAND ROSTER</span>
                <div className="roster-online">
                  <span className="online-dot" />
                  <span>ONLINE</span>
                </div>
              </div>
              <div className="roster-scan" />
            </div>

            {/* Commander row */}
            <div className="commander-row">
              <div className="rank-label rank-commander">★ COMMANDER</div>
              <div className="commander-card">
                <div className="cmdr-emblem">
                  <img src={logo} alt="BlueClan" className="cmdr-logo" />
                  <div className="cmdr-ring" />
                </div>
                <div className="cmdr-info">
                  <div className="cmdr-name">BLUECLAN HQ</div>
                  <div className="cmdr-title">Supreme Technology Division</div>
                  <div className="cmdr-meta">
                    <span className="meta-dot" />Est. 2020 · Global Operations
                  </div>
                </div>
                <div className="cmdr-status">
                  <span className="cmdr-status-dot" />
                  ACTIVE
                </div>
              </div>
            </div>

            <div className="roster-divider">
              <span className="rd-line" /><span className="rd-text">CHAIN OF COMMAND</span><span className="rd-line" />
            </div>

            {/* Captains */}
            <div className="captains-section">
              <div className="rank-label rank-captain">◈ CAPTAINS — {captains.length} DIVISIONS</div>
              <div className="captains-grid">
                {captains.map((c, i) => (
                  <div className="captain-card" key={c.division} style={{ '--cap-color': c.color, animationDelay: `${0.3 + i * 0.1}s` }}>
                    <div className="cap-hex" style={{ borderColor: c.color, color: c.color }}>
                      {c.icon}
                    </div>
                    <div className="cap-division">{c.division}</div>
                    <div className="cap-role" style={{ color: c.color }}>{c.role}</div>
                    <div className="cap-active-dot" style={{ background: c.color }} />
                  </div>
                ))}
              </div>
            </div>

            <div className="roster-divider">
              <span className="rd-line" /><span className="rd-text">FIELD DEPLOYMENT</span><span className="rd-line" />
            </div>

            {/* Troops */}
            <div className="troops-section">
              <div className="troops-header">
                <div className="rank-label rank-troops">◆ TROOPS DEPLOYED</div>
                <span className="troops-count">28 / 32</span>
              </div>
              <div className="troops-bar-track">
                <div className="troops-bar-fill" style={{ width: '87.5%' }} />
                <div className="troops-bar-glow" style={{ left: '87.5%' }} />
              </div>
              <div className="troops-units">
                {['AI Unit', 'Web Unit', 'App Unit', 'DevOps Unit'].map((u, i) => (
                  <div className="troop-unit" key={u}>
                    <span className="tu-dot" style={{ background: i === 0 ? 'var(--cyan)' : i === 1 ? 'var(--purple)' : i === 2 ? 'var(--gold)' : '#4ade80' }} />
                    <span className="tu-name">{u}</span>
                    <span className="tu-count">{[8, 10, 6, 4][i]}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="roster-footer">
              <div className="mission-live">
                <span className="mission-dot" />
                <span className="mission-text">ACTIVE MISSIONS</span>
                <span className="mission-count">{missions}</span>
              </div>
              <div className="roster-coord">
                <span className="font-mono" style={{ fontSize: '10px', color: 'var(--text-muted)', letterSpacing: '1px' }}>
                  LAT 28.6° · LNG 77.2° · GLOBAL OPS
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom ticker */}
      <div className="hero-ticker">
        <div className="ticker-track">
          {Array(4).fill([
            'AI INTELLIGENCE DIVISION', 'WEB FORTRESS TROOP', 'APP BATTALION', 'CLOUD STRIKE UNIT',
            'DEVOPS BRIGADE', 'UI/UX SQUAD', 'ML ENGINEERING CORPS',
          ]).flat().map((t, i) => (
            <span key={i} className="ticker-item">
              <span className="ticker-dot">◆</span> {t}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
